import { useEffect, useMemo, useRef, useState } from "react";
import type {
  Attendance,
  Hasher,
  Kennel,
  Membership,
  Trail,
} from "@hash/shared";
import { displayHares, eventLabel, membershipState } from "@hash/shared";
import { api } from "../api/client";
import { RecordDuesModal } from "../components/RecordDuesModal";
import {
  addHasher,
  loadHashers,
  reloadHashers,
  searchHashers,
  useHashers,
} from "../stores/hasherStore";
import { parseApiError } from "../api/errors";
import { PhoneDialog } from "../components/PhoneDialog";
import { formatTrailDateTimeShort } from "../lib/trailDate";
import { TrailDetailsPage } from "./TrailDetails";
import { getClaims } from "../auth";
import {
  formatMoney,
  rowStatus,
  todayLocalISO,
  type RowStatus,
} from "../lib/checkInStatus";

interface Props {
  trail: Trail;
  // Open straight into the trail-details editor (used by the role-based Home
  // "Edit trail details" action for an upcoming trail you're haring).
  initialShowDetails?: boolean;
}

// A checked-in entry pairs the attendance row (snapshot name, flags, paid bit —
// the source of truth for what's displayed) with the live hasher record (needed
// for "update to current name", the visitor default, and phone-attach).
interface CheckedIn {
  att: Attendance;
  hasher: Hasher;
}

/**
 * The primary check-in surface (Story 8.2), mobile-first:
 *   1. Currently checked in — alphabetical by snapshot name, with a live count,
 *      a paid/unpaid filter, inline phone-attach for lite hashers, and a tap
 *      target that opens the attendance edit modal (Story 8.8).
 *   2. Search over the roster + inline "Create" (Story 8.5).
 *   3. Recent attendees, tappable to check in (Story 8.3).
 */
export function CheckInPage({ trail, initialShowDetails = false }: Props) {
  const [query, setQuery] = useState("");
  const [recent4w, setRecent4w] = useState<Hasher[]>([]);
  const [recent3m, setRecent3m] = useState<Hasher[]>([]);
  // Roster search runs against the shared client-side store, not the server.
  const { hashers: roster } = useHashers();
  const [checkedIn, setCheckedIn] = useState<CheckedIn[]>([]);
  // Membership per checked-in hasher (for member/lapsed badges) and the kennel
  // door fee (for unpaid/paid amounts) — both from GET /trails/:id.
  const [memberships, setMemberships] = useState<Record<string, Membership>>({});
  const [doorFee, setDoorFee] = useState<number | undefined>(undefined);
  // Kennel toggle: when on, virgins skip the standard door fee.
  const [virginsFreeDoor, setVirginsFreeDoor] = useState(false);
  // Server copy of the trail, refreshed when the details editor closes so a
  // special-fee change (Story 7.1) is charged immediately.
  const [liveTrail, setLiveTrail] = useState<Trail>(trail);
  // Hare names for the header, seeded from the list's read-time resolution and
  // refreshed from the GET /trails/:id hasher bundle.
  const [hareNames, setHareNames] = useState<string[]>(trail.hareNames ?? []);
  const [paidFilter, setPaidFilter] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<CheckedIn | null>(null);
  const [phoneTarget, setPhoneTarget] = useState<Hasher | null>(null);
  const [reverseConfirm, setReverseConfirm] = useState<CheckedIn | null>(null);
  const [showDetails, setShowDetails] = useState(initialShowDetails);
  // The hasher we're recording dues for (period picker open), or null. Plus
  // whether the trail's kennel has any scheme at all — when it doesn't, there's
  // nothing to record and the Dues affordance stays hidden.
  const [duesTarget, setDuesTarget] = useState<CheckedIn | null>(null);
  const [duesAvailable, setDuesAvailable] = useState(false);

  const today = todayLocalISO();

  useEffect(() => {
    api.hashers.recent(4).then((r) => setRecent4w(r.hashers));
    api.hashers.recent(13).then((r) => setRecent3m(r.hashers));
    // Ensure the roster store is loaded (and refreshed if stale) for search.
    loadHashers().catch(() => {});
  }, []);

  // GET /trails/:id bundles attendance + hashers + payments + memberships +
  // the kennel (same partition for the first three), so a single round-trip
  // hydrates the checked-in section and its payment badges.
  useEffect(() => {
    if (showDetails) return; // reload on close instead, picking up edits
    let cancelled = false;
    api.trails
      .get(trail.id)
      .then(({ trail: fresh, attendance, hashers, memberships, kennel }) => {
        if (cancelled) return;
        const byId = new Map(hashers.map((h) => [h.id, h]));
        const rows: CheckedIn[] = [];
        for (const att of attendance) {
          const hasher = byId.get(att.hasherId);
          rows.push({ att, hasher: hasher ?? fallbackHasher(att) });
        }
        setLiveTrail(fresh);
        setHareNames(
          (fresh.hareHasherIds ?? [])
            .map((id) => byId.get(id)?.name)
            .filter((n): n is string => Boolean(n))
        );
        setCheckedIn(rows);
        setMemberships(
          Object.fromEntries(memberships.map((m) => [m.hasherId, m]))
        );
        setDoorFee(kennel?.doorFee);
        setVirginsFreeDoor(kennel?.virginsFreeDoor ?? false);
      });
    return () => {
      cancelled = true;
    };
  }, [trail.id, showDetails]);

  // Recording dues is misman-scoped to the trail's kennel (admin ∨ misman of
  // it), matching the server gate — a per-event hare with no misman grant can't
  // record dues, so they never see the affordance.
  const claims = getClaims();
  const canManageDues =
    claims?.role === "admin" ||
    (claims?.mismanKennels ?? []).includes(trail.kennelId);

  // Whether dues are recordable on this kennel: the caller manages it AND at
  // least one non-retired scheme has a currently-purchasable period. Drives the
  // "Dues" affordance for every non-member (lapsed OR never-paid), not just
  // lapsed ones (Story 10.1).
  useEffect(() => {
    if (!canManageDues) {
      setDuesAvailable(false);
      return;
    }
    let cancelled = false;
    api.schemes
      .list(trail.kennelId)
      .then(({ schemes }) => {
        if (cancelled) return;
        setDuesAvailable(
          schemes.some((s) => !s.retired && s.purchasable.length > 0)
        );
      })
      .catch(() => {
        if (!cancelled) setDuesAvailable(false);
      });
    return () => {
      cancelled = true;
    };
  }, [trail.kennelId, canManageDues]);

  // A non-member (lapsed or unpaid) on a kennel that sells dues can have a cash
  // membership recorded inline.
  const canRecordDues = (ci: CheckedIn): boolean =>
    duesAvailable &&
    membershipState(memberships[ci.hasher.id], today) !== "active";

  const statusFor = (ci: CheckedIn): RowStatus =>
    rowStatus({
      membership: memberships[ci.hasher.id],
      paid: ci.att.paid,
      doorFee,
      today,
      trail: liveTrail,
      virgin: ci.att.virgin,
      virginsFreeDoor,
    });

  // Roster search against the local store (recomputes as you type / as the
  // store updates). Empty query → no results, matching the prior behavior.
  const searchResults = useMemo(
    () => searchHashers(query),
    // searchHashers reads the same snapshot `roster` exposes; depend on it so
    // results refresh when the store changes (e.g. a new hasher is added).
    [query, roster]
  );

  const checkedInIds = useMemo(
    () => new Set(checkedIn.map((c) => c.hasher.id)),
    [checkedIn]
  );

  // This trail's hares, for the badge on their checked-in rows.
  const hareIds = useMemo(
    () => new Set(liveTrail.hareHasherIds ?? []),
    [liveTrail]
  );

  // Alphabetical by snapshot name (Story 8.2), then apply the paid filter.
  const checkedInSorted = useMemo(() => {
    // nameSnapshot is typed required, but older attendance rows may omit it;
    // fall back to the live name so the sort never dereferences undefined.
    const sortName = (c: CheckedIn) => c.att.nameSnapshot ?? c.hasher.name;
    const list = [...checkedIn].sort((a, b) =>
      sortName(a).localeCompare(sortName(b))
    );
    // "Unpaid only" means rows that still owe — drop both paid rows and rows
    // with nothing to collect (free trails and comped virgins).
    return paidFilter
      ? list.filter((c) => !c.att.paid && statusFor(c).badge !== "free")
      : list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkedIn, paidFilter, memberships, doorFee, virginsFreeDoor, liveTrail, today]);

  // Per-event reconciliation totals from durable payment state (Story 9.3).
  const totals = useMemo(() => {
    let collected = 0;
    let outstanding = 0;
    for (const ci of checkedIn) {
      const s = statusFor(ci);
      if (s.badge === "paid") collected += s.amount;
      else if (s.badge === "unpaid") outstanding += 1;
    }
    return { collected, outstanding };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkedIn, memberships, doorFee, virginsFreeDoor, liveTrail, today]);

  const recent4wFiltered = useMemo(
    () => recent4w.filter((h) => !checkedInIds.has(h.id)),
    [recent4w, checkedInIds]
  );
  const recent3mFiltered = useMemo(() => {
    const seen = new Set(recent4w.map((h) => h.id));
    return recent3m.filter((h) => !seen.has(h.id) && !checkedInIds.has(h.id));
  }, [recent4w, recent3m, checkedInIds]);
  const searchResultsFiltered = useMemo(
    () => searchResults.filter((h) => !checkedInIds.has(h.id)),
    [searchResults, checkedInIds]
  );

  const upsert = (ci: CheckedIn) =>
    setCheckedIn((s) => {
      const without = s.filter((x) => x.hasher.id !== ci.hasher.id);
      return [...without, ci];
    });

  const addCheckIn = async (hasher: Hasher) => {
    if (checkedInIds.has(hasher.id)) return; // no-op if already in (Story 8.4)
    // Checking someone in from the search results means the search has done its
    // job — clear it so the screen returns to the default view (matches the
    // create-new-hasher flow). A no-op when checking in from recents.
    setQuery("");
    // Optimistic row from what we know; reconcile with the server's snapshot.
    const optimistic: CheckedIn = { att: optimisticAtt(trail, hasher), hasher };
    upsert(optimistic);
    try {
      const att = await api.attendance.checkIn(trail.id, { hasherId: hasher.id });
      upsert({ att, hasher });
    } catch {
      setCheckedIn((s) => s.filter((x) => x.hasher.id !== hasher.id));
      alert("Check-in failed");
    }
  };

  const removeCheckIn = async (ci: CheckedIn) => {
    const prev = checkedIn;
    setCheckedIn((s) => s.filter((x) => x.hasher.id !== ci.hasher.id));
    try {
      await api.attendance.remove(trail.id, ci.hasher.id);
    } catch {
      setCheckedIn(prev);
      alert("Couldn't cancel the check-in");
    }
  };

  // Unpaid -> paid is immediate (Story 8.6); paid -> unpaid is confirmed (8.7).
  // Non-tappable badges (member, free trail) make this a no-op.
  const onBadgeTap = (ci: CheckedIn) => {
    if (!statusFor(ci).tappable) return;
    if (ci.att.paid) setReverseConfirm(ci);
    else void setPaid(ci, true);
  };

  const setPaid = async (ci: CheckedIn, paid: boolean) => {
    upsert({ ...ci, att: { ...ci.att, paid } });
    try {
      await api.attendance.setPaid(trail.id, ci.hasher.id, paid);
    } catch {
      upsert({ ...ci, att: { ...ci.att, paid: !paid } });
    }
  };


  const saveAttPatch = async (
    ci: CheckedIn,
    patch: Partial<Pick<Attendance, "virgin" | "visitor" | "nameSnapshot">>
  ) => {
    const updated: CheckedIn = { ...ci, att: { ...ci.att, ...patch } };
    upsert(updated);
    setEditTarget((t) => (t && t.hasher.id === ci.hasher.id ? updated : t));
    await api.attendance.update(trail.id, ci.hasher.id, patch);
  };

  const isSearching = query.trim().length > 0;

  if (showDetails) {
    return (
      <TrailDetailsPage
        trailId={trail.id}
        hasherId={getClaims()?.hasherId ?? ""}
        onClose={() => setShowDetails(false)}
      />
    );
  }

  return (
    <div className="min-h-screen pb-24">
      <header className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="text-sm font-medium text-slate-900">
            {trail.kennel} {eventLabel(trail)}
          </div>
          <button
            type="button"
            onClick={() => setShowDetails(true)}
            className="text-sm font-medium text-slate-500 hover:text-slate-900"
          >
            Details
          </button>
        </div>
        {/* Date, start location, hares — and deliberately never the on-in:
            this screen is open in front of the pack at the start, and the
            on-in stays a surprise until trail's end. */}
        <div className="text-sm text-slate-500">
          {formatTrailDateTimeShort(liveTrail)}
        </div>
        <div className="text-sm text-slate-500 truncate">
          {liveTrail.startLocation}
        </div>
        {(() => {
          const hares = displayHares({ customHare: liveTrail.customHare, hareNames });
          return hares.length > 0 ? (
            <div className="text-sm text-slate-500 break-words">
              Hares: {hares.join(", ")}
            </div>
          ) : (
            <div className="text-sm text-amber-700 truncate">Hares: TBD</div>
          );
        })()}
        <input
          type="search"
          inputMode="search"
          placeholder="Search hashers…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => {
            loadHashers().catch(() => {});
            // Jump back to the top of the roster so results aren't hidden
            // below the fold if the pack list was scrolled down.
            window.scrollTo({ top: 0 });
          }}
          className="mt-2 w-full h-12 px-4 rounded-lg bg-slate-100 text-base focus:outline-none focus:ring-2 focus:ring-slate-900"
        />
      </header>

      {/* While searching, the results sit above the checked-in list so the
          hasher you're looking for is the first thing under the search box. */}
      {isSearching && (
        <Section title="Search results">
          {searchResultsFiltered.map((h) => (
            <CandidateRow key={h.id} hasher={h} onAdd={() => addCheckIn(h)} />
          ))}
          <AddNewHasherButton onClick={() => setAddOpen(true)} />
        </Section>
      )}

      <section className="mt-2">
        <div className="flex items-center justify-between px-4 pt-3 pb-1">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Checked in ({checkedIn.length})
          </h2>
          {checkedIn.length > 0 && (
            <button
              type="button"
              onClick={() => setPaidFilter((v) => !v)}
              aria-pressed={paidFilter}
              className={`text-xs font-medium rounded-full px-3 py-1 ${
                paidFilter
                  ? "bg-slate-900 text-white"
                  : "bg-slate-100 text-slate-600"
              }`}
            >
              Unpaid only
            </button>
          )}
        </div>
        {checkedIn.length > 0 && (
          <p className="px-4 pb-2 text-xs text-slate-500">
            Collected {formatMoney(totals.collected)} · {totals.outstanding}{" "}
            outstanding
          </p>
        )}
        <ul className="divide-y divide-slate-200 bg-white">
          {checkedInSorted.length === 0 ? (
            <li className="px-4 py-3 text-sm text-slate-400">
              {checkedIn.length === 0
                ? "No one checked in yet."
                : "All checked-in hashers are paid."}
            </li>
          ) : (
            checkedInSorted.map((ci) => (
              <CheckInRow
                key={ci.hasher.id}
                ci={ci}
                isHare={hareIds.has(ci.hasher.id)}
                status={statusFor(ci)}
                onOpenEdit={() => setEditTarget(ci)}
                onBadgeTap={() => onBadgeTap(ci)}
                onAttachPhone={() => setPhoneTarget(ci.hasher)}
                canRecordDues={canRecordDues(ci)}
                onRecordDues={() => setDuesTarget(ci)}
              />
            ))
          )}
        </ul>
      </section>

      {/* Recents stay below the checked-in list at all times — even while
          searching. A hasher may appear both here and in the search results
          above; that overlap is fine. */}
      <Section title="Recent attendees">
        {recent4wFiltered.map((h) => (
          <CandidateRow key={h.id} hasher={h} onAdd={() => addCheckIn(h)} />
        ))}
      </Section>
      {recent3mFiltered.length > 0 && (
        <Section title="Earlier (last 3 months)">
          {recent3mFiltered.map((h) => (
            <CandidateRow key={h.id} hasher={h} onAdd={() => addCheckIn(h)} />
          ))}
        </Section>
      )}

      {addOpen && (
        <AddNewHasherModal
          trail={trail}
          initialName={query.trim()}
          onCheckedIn={(ci) => {
            upsert(ci);
            setAddOpen(false);
            // The just-created hasher is now in the checked-in list, so the
            // search that seeded their name has served its purpose — clear it.
            setQuery("");
          }}
          onClose={() => setAddOpen(false)}
        />
      )}

      {editTarget && (
        <EditAttendanceModal
          ci={editTarget}
          onPatch={(patch) => saveAttPatch(editTarget, patch)}
          onCancelCheckIn={async () => {
            const ci = editTarget;
            setEditTarget(null);
            await removeCheckIn(ci);
          }}
          onClose={() => setEditTarget(null)}
        />
      )}

      {phoneTarget && (
        <PhoneDialog
          title="Add phone number"
          hint={`Vouch for ${phoneTarget.name} so they can log in. No code is sent.`}
          submitLabel="Attach"
          onClose={() => setPhoneTarget(null)}
          onSubmit={async (e164) => {
            const res = await api.hashers.setPhone(phoneTarget.id, e164);
            setCheckedIn((s) =>
              s.map((c) =>
                c.hasher.id === phoneTarget.id
                  ? {
                      ...c,
                      hasher: {
                        ...c.hasher,
                        phone: res.phone,
                        accountState: res.accountState,
                      },
                    }
                  : c
              )
            );
            setPhoneTarget(null);
          }}
        />
      )}

      {duesTarget && (
        <RecordDuesModal
          hasher={duesTarget.hasher}
          fixedKennel={{ id: liveTrail.kennelId, name: liveTrail.kennel }}
          onClose={() => setDuesTarget(null)}
          onRecorded={(_kennelId, membership) => {
            setMemberships((m) => ({
              ...m,
              [duesTarget.hasher.id]: membership,
            }));
            setDuesTarget(null);
          }}
        />
      )}

      {reverseConfirm && (
        <ConfirmModal
          title={`Mark ${reverseConfirm.att.nameSnapshot ?? reverseConfirm.hasher.name} as unpaid?`}
          confirmLabel="Mark unpaid"
          onConfirm={async () => {
            const ci = reverseConfirm;
            setReverseConfirm(null);
            await setPaid(ci, false);
          }}
          onCancel={() => setReverseConfirm(null)}
        />
      )}
    </div>
  );
}

function CheckInRow({
  ci,
  isHare,
  status,
  onOpenEdit,
  onBadgeTap,
  onAttachPhone,
  canRecordDues,
  onRecordDues,
}: {
  ci: CheckedIn;
  isHare: boolean;
  status: RowStatus;
  onOpenEdit: () => void;
  onBadgeTap: () => void;
  onAttachPhone: () => void;
  canRecordDues: boolean;
  onRecordDues: () => void;
}) {
  const { att, hasher } = ci;
  // Fall back to the live name when an older attendance row has no nameSnapshot.
  const displayName = att.nameSnapshot ?? hasher.name;
  // Lite, no-phone hashers get an inline phone-attach affordance (Story 8.2).
  const needsPhone = hasher.accountState !== "claimed" && !hasher.phone;

  return (
    <li className="flex items-center bg-emerald-50/60">
      <button
        type="button"
        onClick={onOpenEdit}
        className="flex-1 min-w-0 text-left px-4 py-3"
        aria-label={`Edit ${displayName}'s check-in`}
      >
        <div className="flex items-center gap-2">
          <span className="font-medium text-slate-900 truncate">
            {displayName}
          </span>
          {isHare && <Tag color="emerald">hare</Tag>}
          {att.virgin && <Tag color="violet">virgin</Tag>}
          {att.visitor && <Tag color="sky">visitor</Tag>}
          {status.lapsed && <Tag color="amber">lapsed</Tag>}
        </div>
        <div className="text-sm text-slate-500 truncate">{hasher.homeKennel}</div>
      </button>

      {/* Non-members (lapsed or never-paid) can pay dues inline to go free at
          the door (Story 10.1) — a period picker opens for the choice. */}
      {canRecordDues && (
        <button
          type="button"
          onClick={onRecordDues}
          className="mr-1 rounded-lg px-2 py-1 text-xs font-medium text-amber-700 hover:bg-amber-50"
        >
          Dues
        </button>
      )}

      {needsPhone && (
        <button
          type="button"
          onClick={onAttachPhone}
          aria-label={`Add phone for ${hasher.name}`}
          className="w-11 h-11 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-900 active:bg-slate-100"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
          </svg>
        </button>
      )}

      <PaymentBadge status={status} onTap={onBadgeTap} name={displayName} />
    </li>
  );
}

function PaymentBadge({
  status,
  onTap,
  name,
}: {
  status: RowStatus;
  onTap: () => void;
  name: string;
}) {
  if (status.badge === "free") {
    // A comped row (virgins-free waiver) shows an unclickable "Free" pill so it
    // reads as handled, not blank. A genuinely free trail still renders nothing.
    if (!status.comped) return null;
    return (
      <span className="mr-4 ml-1 rounded-full bg-slate-100 px-3 py-1.5 text-sm font-medium text-slate-500">
        Free
      </span>
    );
  }
  if (status.badge === "member") {
    return (
      <span className="mr-4 ml-1 rounded-full bg-emerald-100 px-3 py-1.5 text-sm font-medium text-emerald-700">
        Member
      </span>
    );
  }
  const paid = status.badge === "paid";
  return (
    <button
      type="button"
      onClick={onTap}
      aria-label={`${paid ? "Mark unpaid" : "Mark paid"}: ${name}`}
      className={`mr-4 ml-1 px-3 py-1.5 rounded-full text-sm font-medium ${
        paid
          ? "bg-emerald-600 text-white"
          : "bg-white border border-slate-300 text-slate-700"
      }`}
    >
      {paid ? "Paid" : `Unpaid ${formatMoney(status.amount)}`}
    </button>
  );
}

function CandidateRow({
  hasher,
  onAdd,
}: {
  hasher: Hasher;
  onAdd: () => void;
}) {
  return (
    <li className="flex items-center px-4 py-3">
      <div className="flex-1 min-w-0">
        <div className="font-medium text-slate-900 truncate">{hasher.name}</div>
        <div className="text-sm text-slate-500 truncate">{hasher.homeKennel}</div>
      </div>
      <button
        type="button"
        onClick={onAdd}
        aria-label={`Check in ${hasher.name}`}
        className="ml-3 w-11 h-11 flex items-center justify-center rounded-full text-xl font-semibold bg-slate-900 text-white active:bg-slate-700 active:scale-95"
      >
        +
      </button>
    </li>
  );
}

function Tag({
  children,
  color,
}: {
  children: React.ReactNode;
  color: "violet" | "sky" | "amber" | "emerald";
}) {
  const map = {
    violet: "bg-violet-100 text-violet-700",
    sky: "bg-sky-100 text-sky-700",
    amber: "bg-amber-100 text-amber-700",
    emerald: "bg-emerald-100 text-emerald-700",
  } as const;
  return (
    <span
      className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${map[color]}`}
    >
      {children}
    </span>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mt-2">
      <h2 className="px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
        {title}
      </h2>
      <ul className="divide-y divide-slate-200 bg-white">{children}</ul>
    </section>
  );
}

function AddNewHasherButton({ onClick }: { onClick: () => void }) {
  return (
    <li className="px-4 py-3 bg-white">
      <button
        type="button"
        onClick={onClick}
        className="w-full text-left text-slate-900 font-medium"
      >
        + Create new hasher
      </button>
    </li>
  );
}

/** Edit attendance attributes / cancel a check-in (Story 8.8). */
function EditAttendanceModal({
  ci,
  onPatch,
  onCancelCheckIn,
  onClose,
}: {
  ci: CheckedIn;
  onPatch: (
    patch: Partial<Pick<Attendance, "virgin" | "visitor" | "nameSnapshot">>
  ) => Promise<void>;
  onCancelCheckIn: () => void;
  onClose: () => void;
}) {
  const { att, hasher } = ci;
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [busy, setBusy] = useState(false);
  // Fall back to the live name when an older attendance row has no nameSnapshot.
  const displayName = att.nameSnapshot ?? hasher.name;
  const nameDiffers = hasher.name && hasher.name !== att.nameSnapshot;

  const patch = async (
    p: Partial<Pick<Attendance, "virgin" | "visitor" | "nameSnapshot">>
  ) => {
    setBusy(true);
    try {
      await onPatch(p);
    } finally {
      setBusy(false);
    }
  };

  if (confirmCancel) {
    return (
      <ConfirmModal
        title={`Cancel ${displayName}'s check-in?`}
        body="This removes their attendance (and any payment) for this event."
        confirmLabel="Cancel check-in"
        onConfirm={onCancelCheckIn}
        onCancel={() => setConfirmCancel(false)}
      />
    );
  }

  return (
    <Sheet onClose={onClose} title={displayName}>
      {nameDiffers && (
        <div className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
          This hasher's current name is{" "}
          <span className="font-medium">{hasher.name}</span>.
          <button
            type="button"
            disabled={busy}
            onClick={() => patch({ nameSnapshot: hasher.name })}
            className="ml-1 font-medium underline disabled:opacity-50"
          >
            Update to current name
          </button>
        </div>
      )}

      <ToggleRow
        label="Virgin"
        hint="First-ever trail for this hasher"
        checked={att.virgin}
        disabled={busy}
        onChange={(v) => patch({ virgin: v })}
      />
      <ToggleRow
        label="Visitor"
        hint="Visiting from another kennel"
        checked={att.visitor}
        disabled={busy}
        onChange={(v) => patch({ visitor: v })}
      />

      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={() => setConfirmCancel(true)}
          className="flex-1 h-12 rounded-lg border border-rose-300 text-rose-700 font-medium active:bg-rose-50"
        >
          Cancel check-in
        </button>
        <button
          type="button"
          onClick={onClose}
          className="flex-1 h-12 rounded-lg bg-slate-900 text-white font-medium active:bg-slate-700"
        >
          Done
        </button>
      </div>
    </Sheet>
  );
}

const NEW_KENNEL = "__new__";

/** Create a lite hasher and check them in (Story 8.5). */
function AddNewHasherModal({
  trail,
  initialName,
  onCheckedIn,
  onClose,
}: {
  trail: Trail;
  initialName: string;
  onCheckedIn: (ci: CheckedIn) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(initialName);
  // Seed the list with the trail's own kennel so the first render already
  // resolves `selectedKennel` — a trail's kennel is the managed/organizing one
  // (reference kennels only exist to attribute visitors and don't host trails),
  // so the visitor default starts at false instead of flapping true→false once
  // the full list loads. The async load below replaces this with the real rows.
  const [kennels, setKennels] = useState<Kennel[]>(() => [
    {
      id: trail.kennelId,
      name: trail.kennel,
      managed: true,
      createdAt: trail.createdAt,
    },
  ]);
  const [kennelChoice, setKennelChoice] = useState<string>(trail.kennelId);
  const [newKennelName, setNewKennelName] = useState("");
  const [virgin, setVirgin] = useState(false);
  // Visitor toggle: tracks whether the hare has overridden the kennel-derived
  // default. Until they do, it mirrors the selected kennel's managed-ness.
  const [visitorOverride, setVisitorOverride] = useState<boolean | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    nameRef.current?.focus();
    nameRef.current?.select();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const collected: Kennel[] = [];
      let next: string | undefined = undefined;
      do {
        const res = await api.kennels.list({ cursor: next, limit: 200 });
        if (cancelled) return;
        collected.push(...res.kennels);
        next = res.cursor;
      } while (next);
      if (cancelled) return;
      setKennels(collected);
      if (!collected.find((k) => k.id === trail.kennelId) && collected[0]) {
        setKennelChoice(collected[0].id);
      }
    })().catch((e) => {
      console.error("Failed to load kennels", e);
      setError("Failed to load kennels");
    });
    return () => {
      cancelled = true;
    };
  }, [trail.kennelId]);

  const isNewKennel = kennelChoice === NEW_KENNEL;
  const selectedKennel = kennels.find((k) => k.id === kennelChoice);
  // Default: reference kennel -> visitor, managed -> local. New kennel is a
  // reference kennel, so visitor defaults true.
  const visitorDefault = isNewKennel ? true : selectedKennel?.managed !== true;
  const visitor = visitorOverride ?? visitorDefault;

  const trimmedName = name.trim();
  const trimmedNewKennel = newKennelName.trim();
  const canSubmit =
    !submitting &&
    trimmedName.length > 0 &&
    (isNewKennel ? trimmedNewKennel.length > 0 : kennelChoice.length > 0);

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      let homeKennelId = kennelChoice;
      if (isNewKennel) {
        const match = kennels.find(
          (k) => k.name.toLowerCase() === trimmedNewKennel.toLowerCase()
        );
        homeKennelId = match
          ? match.id
          : (await api.kennels.create({ name: trimmedNewKennel })).id;
      }
      const hasher = await api.hashers.create({
        name: trimmedName,
        homeKennelId,
        visitor,
      });
      // Mirror the new hasher into the shared store so other searches see it.
      addHasher(hasher);
      // Check in with the modal's flags; snapshot the entered name.
      const att = await api.attendance.checkIn(trail.id, {
        hasherId: hasher.id,
        nameSnapshot: trimmedName,
        virgin,
        visitor,
      });
      onCheckedIn({ att, hasher });
    } catch (e) {
      // 409 = the name already exists; refresh the store so a re-search finds it.
      if (parseApiError(e).status === 409) void reloadHashers();
      setError(e instanceof Error ? e.message : "Failed to add hasher");
      setSubmitting(false);
    }
  };

  return (
    <Sheet onClose={onClose} title="Create new hasher">
      <Field label="Hash name">
        <input
          ref={nameRef}
          value={name}
          onChange={(e) => setName(e.target.value)}
          className={inputCls}
        />
      </Field>
      <Field label="Home kennel">
        <select
          value={kennelChoice}
          onChange={(e) => {
            setKennelChoice(e.target.value);
            // Reset the override so the visitor default re-derives from the
            // newly selected kennel (Story 8.5).
            setVisitorOverride(null);
          }}
          className={inputCls}
        >
          {kennels.map((k) => (
            <option key={k.id} value={k.id}>
              {k.name}
            </option>
          ))}
          <option value={NEW_KENNEL}>+ New kennel…</option>
        </select>
        {isNewKennel && (
          <input
            value={newKennelName}
            onChange={(e) => setNewKennelName(e.target.value)}
            placeholder="New kennel name"
            className={`${inputCls} mt-2`}
            autoFocus
          />
        )}
      </Field>

      <ToggleRow
        label="Virgin"
        hint="First-ever trail"
        checked={virgin}
        onChange={setVirgin}
      />
      <ToggleRow
        label="Visitor"
        hint="Visiting from another kennel"
        checked={visitor}
        onChange={(v) => setVisitorOverride(v)}
      />

      {error && (
        <p className="text-sm text-rose-600" role="alert">
          {error}
        </p>
      )}

      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onClose}
          className="flex-1 h-12 rounded-lg border border-slate-300 text-slate-700 font-medium active:bg-slate-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={!canSubmit}
          className="flex-1 h-12 rounded-lg bg-slate-900 text-white font-medium active:bg-slate-700 disabled:bg-slate-300"
        >
          {submitting ? "Adding…" : "Create & check in"}
        </button>
      </div>
    </Sheet>
  );
}

// --- Small shared UI ------------------------------------------------------

function Sheet({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-30 bg-slate-900/40 flex items-end sm:items-center justify-center"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl p-4 max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full text-slate-500 active:bg-slate-100"
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="space-y-3">{children}</div>
      </div>
    </div>
  );
}

function ConfirmModal({
  title,
  body,
  confirmLabel,
  onConfirm,
  onCancel,
}: {
  title: string;
  body?: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-40 bg-slate-900/40 flex items-end sm:items-center justify-center"
      onClick={onCancel}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-base font-semibold mb-1">{title}</h2>
        {body && <p className="text-sm text-slate-600 mb-4">{body}</p>}
        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 h-12 rounded-lg border border-slate-300 text-slate-700 font-medium active:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="flex-1 h-12 rounded-lg bg-rose-600 text-white font-medium active:bg-rose-700"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function ToggleRow({
  label,
  hint,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className="flex w-full items-center justify-between rounded-lg border border-slate-200 px-3 py-3 text-left disabled:opacity-50"
    >
      <span>
        <span className="block font-medium text-slate-900">{label}</span>
        {hint && <span className="block text-xs text-slate-500">{hint}</span>}
      </span>
      <span
        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
          checked ? "bg-emerald-600" : "bg-slate-300"
        }`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
            checked ? "translate-x-5" : "translate-x-0.5"
          }`}
        />
      </span>
    </button>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}

const inputCls =
  "w-full h-12 px-3 rounded-lg border border-slate-300 text-base focus:outline-none focus:ring-2 focus:ring-slate-900";

// Build the attendance row we optimistically show before the server responds.
function optimisticAtt(trail: Trail, hasher: Hasher): Attendance {
  return {
    trailId: trail.id,
    hasherId: hasher.id,
    nameSnapshot: hasher.name,
    paid: false,
    virgin: false,
    visitor: hasher.visitor ?? false,
    checkedInAt: new Date().toISOString(),
    checkedInByHasherId: "",
  };
}

// Minimal hasher stand-in when the trail's hasher row is somehow missing.
function fallbackHasher(att: Attendance): Hasher {
  return {
    id: att.hasherId,
    name: att.nameSnapshot,
    homeKennelId: "",
    homeKennel: "",
    role: "hare",
    visitor: att.visitor,
    accountState: "claimed",
    createdAt: att.checkedInAt,
  };
}
